import React from "react";
import Slider from "react-slick";
import { getDate } from "@services/dayjs";

import { carouselConfigMobile, carouselConfigWeb, mobileScreenType } from "@stadium/constants";

import { GetScreenType } from "@stadium/helpers/device";

import { textResolver } from "@sendmagic/prismic-nextjs/src/constants/text-resolver";
import { checkContentPresence } from "@sendmagic/prismic-nextjs/src/helpers/prismic";
import PrismicLinkTag from "@sendmagic/prismic-nextjs/src/components/LinkTag";
import PrismicImgix from "@sendmagic/prismic-nextjs/src/components/Imgix";
import { appendBackgroundImage } from "@sendmagic/prismic-nextjs/src/helpers/images";

import bgImg from "@sendmagic/prismic-nextjs/src/assets/images/calendar-icon.png";

const responseSliderSettings = [
  {
    breakpoint: 992,
    settings: {
      ...carouselConfigMobile,
      slidesToShow: 2,
      slidesToScroll: 1,
      dots: true,
      touchMove: true,
      swipeToSlide: true,
    },
  },
  {
    breakpoint: 767,
    settings: {
      ...carouselConfigMobile,
      slidesToShow: 1,
      slidesToScroll: 1,
      dots: true,
      touchMove: true,
      swipeToSlide: true,
    },
  },
];

const carouselConfig = {
  ...carouselConfigWeb,
  slidesToShow: 3,
  slidesToScroll: 1,
  dots: true,
  responsive: responseSliderSettings,
  touchMove: true,
};

const backgroundStyle = {
  ...appendBackgroundImage(bgImg),
  backgroundColor: "transparent",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
};

const renderScrollView = (doc, isMobileView) => {
  const { heading, description, main_cta_link, main_cta_text } = doc?.primary || {};
  return (
    <div className="mb-4 mb-md-5 pb-4">
      {isMobileView && (
        <div className="text-slide d-md-none mb-4">
          <div className="">
            {checkContentPresence(heading) && (
              <div className="navy-blue text-theme-primary sns-h2 same-size-title">
                {textResolver(heading)}
              </div>
            )}
            {checkContentPresence(description) && (
              <div className="mt-3 lh-1-point-7">{textResolver(description)}</div>
            )}
            {checkContentPresence(main_cta_text) && (
              <div className="mt-3">
                <PrismicLinkTag
                  urlText={main_cta_text}
                  url={main_cta_link?.url}
                  target={main_cta_link?.target}
                  className="font-weight-bold cta-green-18 text-uppercase program-link"
                />
              </div>
            )}
          </div>
        </div>
      )}

      <Slider {...carouselConfig}>
        {!isMobileView && (
          <div className="scrollable-gallery-slide text-slide">
            <div className="gallery-content-slide-holder">
              {checkContentPresence(heading) && (
                <div className="navy-blue sns-h2 scrollable-text-slide-title same-size-title">
                  {textResolver(heading)}
                </div>
              )}
              {checkContentPresence(description) && (
                <div className="mt-3 scrollable-text-slide-description">
                  {textResolver(description)}
                </div>
              )}
              {checkContentPresence(main_cta_text) && (
                <div className="mt-3">
                  <PrismicLinkTag
                    urlText={main_cta_text}
                    url={main_cta_link?.url}
                    target={main_cta_link?.target}
                    className="font-weight-bold cta-green-18 text-uppercase program-link"
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {doc.items.map(
          (
            { image, caption_description, caption_heading, cta_link, cta_link_text, date },
            index,
          ) => {
            const isClickableSlide = cta_link?.url;
            return (
              <div className="scrollable-gallery-slide" key={`image-${index}`}>
                {isClickableSlide && (
                  <PrismicLinkTag
                    url={cta_link?.url}
                    target={cta_link?.target}
                    className="slide-block-link"
                  />
                )}
                <div className="gallery-image-block">
                  {checkContentPresence(date) && (
                    <div className="gallery-date-holder" style={backgroundStyle}>
                      {getDate(date)}
                    </div>
                  )}
                  <PrismicImgix
                    className="img-responsive img-responsive-height"
                    src={image?.url}
                    alt={image?.alt}
                    desktop="450px"
                    mobile="300px"
                    tablet="240px"
                    largeDisplay="900px"
                  />
                </div>
                <div className="gallery-content-block">
                  {checkContentPresence(caption_heading) && (
                    <div className="font-weight-bold sns-h4 gallery-content-title">
                      {textResolver(caption_heading)}
                    </div>
                  )}
                  {checkContentPresence(caption_description) && (
                    <div className="mt-2 gallery-content-description">
                      {textResolver(caption_description)}
                    </div>
                  )}
                  {isClickableSlide && (
                    <div className="mt-3 gallery-content-link-row">
                      <div className="font-weight-bold cta-green-18 text-uppercase program-link">
                        {textResolver(cta_link_text)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          },
        )}
      </Slider>
    </div>
  );
};

const renderCalendarView = (doc) => {
  const { heading, description, main_cta_link, main_cta_text } = doc?.primary || {};

  return (
    <>
      {(checkContentPresence(heading) || checkContentPresence(description)) && (
        <div className="container text-slide mb-4">
          <div className="">
            {checkContentPresence(heading) && (
              <div className="navy-blue text-theme-primary sns-h2 same-size-title scrollable-text-slide-heading">
                {textResolver(heading)}
              </div>
            )}
            {checkContentPresence(description) && (
              <div className="mt-3 lh-1-point-7">{textResolver(description)}</div>
            )}
          </div>
        </div>
      )}

      <div className="container container-width no-slider">
        <div className="row d-flex justify-content-start row-cols-sm-2 row-cols-md-3 row-cols-lg-3">
          {doc.items.map(
            (
              { image, caption_description, caption_heading, cta_link, cta_link_text, date },
              index,
            ) => {
              const isClickableSlide = cta_link?.url;
              return (
                <div className="col-12 col-md-6 col-xl-4 py-3 px-3" key={`image-${index}`}>
                  <div className="scrollable-gallery-slide">
                    {isClickableSlide && (
                      <PrismicLinkTag
                        url={cta_link?.url}
                        target={cta_link?.target}
                        className="slide-block-link"
                      />
                    )}
                    <div className="">
                      <div className="gallery-image-block">
                        {checkContentPresence(date) && (
                          <div className="gallery-date-holder" style={backgroundStyle}>
                            {getDate(date)}
                          </div>
                        )}
                        <PrismicImgix
                          className="img-responsive img-responsive-height"
                          src={image?.url}
                          alt={image?.alt}
                          desktop="450px"
                          mobile="300px"
                          tablet="240px"
                          largeDisplay="900px"
                        />
                      </div>
                      <div className="gallery-content-block">
                        {checkContentPresence(caption_heading) && (
                          <div className="font-weight-bold sns-h4 gallery-content-title">
                            {textResolver(caption_heading)}
                          </div>
                        )}
                        {checkContentPresence(caption_description) && (
                          <div className="mt-2 gallery-content-description">
                            {textResolver(caption_description)}
                          </div>
                        )}
                        {isClickableSlide && (
                          <div className="mt-3 gallery-content-link-row">
                            <div className="font-weight-bold cta-green-18 text-uppercase program-link">
                              {textResolver(cta_link_text)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            },
          )}
        </div>
      </div>

      {checkContentPresence(main_cta_text) && (
        <div className="mt-3 text-center ">
          <PrismicLinkTag
            urlText={main_cta_text}
            url={main_cta_link?.url}
            target={main_cta_link?.target}
            className="font-weight-bold cta-green-18 text-uppercase program-link"
          />
        </div>
      )}
    </>
  );
};

const HorizontalScrollableGallery = ({ doc }) => {
  const { internal_link_id, bg_color, enable_scrolling } = doc?.primary || {};
  const isMobileView = typeof window !== "undefined" && GetScreenType() === mobileScreenType;
  return (
    <div
      className="scrollable-gallery-slice overflow-hidden prismic-slice"
      style={{ background: bg_color }}
    >
      <div className="container-fluid container-width">
        {internal_link_id && (
          <span id={internal_link_id} className="scroll-point position-relative d-block" />
        )}

        {enable_scrolling ? renderScrollView(doc, isMobileView) : renderCalendarView(doc)}
      </div>
    </div>
  );
};

export default HorizontalScrollableGallery;
